import React from "react";
import {observer} from "mobx-react-lite";
import EditField from "../../molecules/editField/editField";
import {DateObservable} from "../../../stores/observables/dateObservable";
import DatePicker from "react-datepicker";
import {trimSeconds} from "../../../utils/dateUtils";
import classNames from "classnames";
import setHours from "date-fns/setHours";
import setMinutes from "date-fns/setMinutes";
import {utcToZonedTime, zonedTimeToUtc} from "date-fns-tz";
import * as Popper from "@popperjs/core";

export interface IDateEditProps {
  value: DateObservable;
  label?: string;
  validationMessage?: string;
  overrideAsInvalid?: boolean;
  overrideIsRequired?: boolean;
  dateFormat?: string;
  disabled?: boolean;
  placeholder?: string;
  openToDate?: Date | null;
  helpText?: string;
  timeZone: string;
  showTimeSelect?: boolean;
  preventManualEntry?: boolean;
  enabledDates?: Date[];
  minDate?: Date | null;
  onChange?: () => void;
  showTimeSelectOnly?: boolean;
  disableMinTime?: boolean;
  maxDate?: Date | null;
  showMonthYearPicker?: boolean;
  position?: Popper.Placement;
}

const DateEdit: React.FC<IDateEditProps> = ({
  value,
  label,
  validationMessage,
  overrideIsRequired,
  dateFormat,
  disabled,
  placeholder,
  openToDate,
  showTimeSelect = true,
  helpText,
  preventManualEntry,
  timeZone,
  enabledDates,
  minDate,
  onChange,
  showTimeSelectOnly,
  disableMinTime,
  maxDate,
  showMonthYearPicker = false,
  position = "auto"
}) => {
  const isRequired: boolean = (() => {
    switch (overrideIsRequired) {
      case null:
      // falls through to next statement
      case undefined:
        return value.validate != null;
      default:
        return overrideIsRequired;
    }
  })();

  const getMinTime = () => {
    return disableMinTime
      ? new Date(new Date().setHours(0, 0, 0, 0))
      : value.calculateMinDate(timeZone);
  };

  return (
    <EditField
      label={label}
      validationMessage={validationMessage}
      showValidationMessage={!value.isValid}
      isRequired={isRequired}
      helpText={helpText}
    >
      <DatePicker
        disabled={disabled}
        className={classNames("form-control", {
          "is-invalid": !value.isValid,
          "is-disabled": disabled
        })}
        showTimeSelectOnly={showTimeSelectOnly}
        selected={value.date && utcToZonedTime(value.date, timeZone)}
        onChange={(val: Date) => {
          if (val) {
            value.setDate(trimSeconds(zonedTimeToUtc(val, timeZone)));
            // reset selected time to minimum valid time if required
            const minTime = getMinTime();
            if (value.minDate && !disableMinTime && minTime > val) {
              value.setDate(trimSeconds(zonedTimeToUtc(minTime, timeZone)));
            }
          }
          if (onChange) {
            onChange();
          }
        }}
        dateFormat={dateFormat != null ? dateFormat : "dd/MM/yyyy hh:mm a"}
        showTimeSelect={showTimeSelect}
        timeIntervals={15}
        minDate={minDate || value.minDate}
        maxDate={maxDate}
        minTime={getMinTime()}
        maxTime={setHours(
          setMinutes(zonedTimeToUtc(new Date(), timeZone), 59),
          23
        )}
        popperPlacement={position}
        calendarStartDay={1}
        placeholderText={placeholder}
        includeDates={enabledDates}
        openToDate={openToDate ?? undefined}
        onKeyDown={(e) => {
          if (preventManualEntry) {
            e.preventDefault();
          }
        }}
        showPopperArrow={false}
        showMonthYearPicker={showMonthYearPicker}
      />
    </EditField>
  );
};
export default observer(DateEdit);
